/**
 * In-chat search results — `GET /messages/search`, scoped to ONE channel.
 *
 * Two response modes, two behaviours:
 *  - `message_id`: the query was a bare ASCII number AND a message with that
 *    id exists here AND it passes the current attachment filter. One hit,
 *    nothing to list — the panel jumps the message window straight to it.
 *  - `text`: a LIKE match over `message_text`, newest first, paged by the
 *    keyset cursor `next_before_id`.
 *
 * The mode is READ FROM THE RESPONSE, never predicted from the query. It is
 * undecidable here: `500` searches as text when message 500 does not exist or
 * the media filter excludes it, Arabic-Indic digits (٢٠٢٤) always search as
 * text, and any request carrying `before_id` is always text.
 *
 * Search runs on submit, never per keystroke: the backend has no FULLTEXT
 * index on Messages, so every query is a LIKE scan of one chat's partition.
 */

import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { MediaCategory, Message } from '@/types/api';
import { searchMessages } from '@/api/messages';
import { getErrorMessage } from '@/api/client';
import { useChatTheme } from '../ChatThemeContext';

interface MessageSearchPanelProps {
  chatId: number;
  chatName: string;
  /** Kept in sync with the message panel's own filter, so results match the view. */
  mediaType: MediaCategory | null;
  onJump: (messageId: number) => void;
  onClose: () => void;
}

const PAGE_SIZE = 50;

const formatStamp = (value: string | null): string => {
  if (!value) return '';
  try {
    return format(parseISO(value), 'yyyy-MM-dd HH:mm');
  } catch {
    return '';
  }
};

/** Case-insensitive literal highlight — no regex, so the term needs no escaping. */
const highlight = (text: string, term: string, color: string): React.ReactNode => {
  if (!term) return text;
  const haystack = text.toLowerCase();
  const needle = term.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let found = haystack.indexOf(needle, cursor);
  let key = 0;
  while (found !== -1) {
    if (found > cursor) parts.push(text.slice(cursor, found));
    parts.push(
      <Box key={key++} component="mark" sx={{ bgcolor: color, color: 'inherit', px: 0.25 }}>
        {text.slice(found, found + term.length)}
      </Box>
    );
    cursor = found + term.length;
    found = haystack.indexOf(needle, cursor);
  }
  parts.push(text.slice(cursor));
  return parts;
};

const SNIPPET_LENGTH = 240;

const MessageSearchPanel: React.FC<MessageSearchPanelProps> = ({
  chatId,
  chatName,
  mediaType,
  onJump,
  onClose,
}) => {
  const { t } = useTranslation('pages');
  const { colors } = useChatTheme();

  const [input, setInput] = useState('');
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Message[] | null>(null);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Two-part error: a translated sentence plus whatever the API actually said,
  // kept in its own LTR box so an English detail cannot scramble the Arabic.
  const [error, setError] = useState<{ message: string; detail?: string } | null>(null);
  // Every query captures this on the way out and re-checks it on the way back:
  // a LIKE scan of one chat can easily be overtaken by a shorter one, and the
  // loser must not leave its results — or its `next_before_id` cursor — behind.
  const searchTokenRef = useRef(0);

  const runSearch = useCallback(
    async (query: string, beforeId: number | null) => {
      // Mirrors the endpoint's own 400s so the obvious mistakes never become a
      // round trip. `\d` is ASCII-only in JS, which is exactly the jump rule
      // server-side: an Arabic-Indic '٢' is one character of text, not an id.
      const trimmed = query.trim();
      if (!trimmed) {
        setError({ message: t('chatPreview.search.errors.empty') });
        return;
      }
      if (trimmed.length < 2 && !/^[0-9]+$/.test(trimmed)) {
        setError({ message: t('chatPreview.search.errors.tooShort') });
        return;
      }

      setError(null);
      const token = ++searchTokenRef.current;
      // Both flags are set on every start, not just the one this request owns:
      // the superseded request's `finally` is token-guarded and will not clear
      // anything, so nothing else would ever put the other one back to false.
      setIsSearching(beforeId === null);
      setIsLoadingMore(beforeId !== null);
      if (beforeId === null) {
        setResults(null);
      }

      try {
        const resp = await searchMessages({
          chat_id: chatId,
          q: trimmed,
          limit: PAGE_SIZE,
          before_id: beforeId ?? undefined,
          media_type: mediaType ?? undefined,
        });

        if (searchTokenRef.current !== token) return; // a newer query owns the panel

        if (resp.mode === 'message_id' && resp.messages.length > 0) {
          // Not a result list — one exact hit. Hand it to the message window.
          onJump(resp.messages[0].message_id);
          return;
        }

        setTerm(trimmed);
        setElapsedMs(resp.elapsed_ms);
        setHasMore(resp.has_more);
        setNextBeforeId(resp.next_before_id);
        setResults((prev) => (beforeId === null ? resp.messages : [...(prev ?? []), ...resp.messages]));
      } catch (e) {
        if (searchTokenRef.current !== token) return;
        // The one 400 the pre-checks cannot rule out: a single digit that is
        // not an existing id in this chat is too short to text-search.
        if (trimmed.length < 2) {
          setError({ message: t('chatPreview.search.errors.tooShort') });
        } else {
          setError({ message: t('chatPreview.search.errors.failed'), detail: getErrorMessage(e) });
        }
      } finally {
        // Only the query that still owns the panel clears its spinners.
        if (searchTokenRef.current === token) {
          setIsSearching(false);
          setIsLoadingMore(false);
        }
      }
    },
    [chatId, mediaType, onJump, t]
  );

  const rowSx = {
    px: 1.5,
    py: 1,
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.searchBorder}`,
    '&:hover': { bgcolor: colors.sidebarItemHover },
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: colors.chatBg,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Search box + the two things a reviewer must know: what is searched, and what is not */}
      <Box sx={{ p: 1.5, bgcolor: colors.sidebarHeaderBg, borderBottom: `1px solid ${colors.searchBorder}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              bgcolor: colors.searchBg,
              border: `1px solid ${colors.searchBorder}`,
              borderRadius: '20px',
              px: 1.5,
              py: 0.5,
            }}
          >
            <SearchIcon sx={{ color: colors.searchText, mr: 1, fontSize: 20 }} />
            <InputBase
              autoFocus
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Same gate as the button: Enter must not stack queries.
                if (e.key === 'Enter' && !isSearching && input.trim() !== '') {
                  runSearch(input, null);
                }
              }}
              placeholder={t('chatPreview.search.placeholder')}
              inputProps={{ maxLength: 200, 'aria-label': t('chatPreview.search.placeholder') }}
              fullWidth
              sx={{
                color: colors.sidebarText,
                fontSize: '0.875rem',
                '& .MuiInputBase-input::placeholder': { color: colors.searchText, opacity: 1 },
              }}
            />
          </Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => runSearch(input, null)}
            disabled={isSearching || input.trim() === ''}
          >
            {t('chatPreview.search.submit')}
          </Button>
          <Tooltip title={t('chatPreview.search.close')}>
            <IconButton size="small" onClick={onClose} sx={{ color: colors.searchText }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: colors.searchText }}>
          {t('chatPreview.search.scope', { channel: `⁨${chatName}⁩` })}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: colors.searchText }}>
          {t('chatPreview.search.textOnlyNote')}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: colors.searchText }}>
          {t('chatPreview.search.numberNote')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ m: 1.5 }} onClose={() => setError(null)}>
          {error.message}
          {error.detail && (
            <Box component="span" dir="ltr" sx={{ display: 'block', mt: 0.5, opacity: 0.8, fontSize: '0.75rem' }}>
              {error.detail}
            </Box>
          )}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {isSearching ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 4 }}>
            <CircularProgress size={24} sx={{ color: colors.bubbleTimestamp }} />
            <Typography variant="body2" sx={{ color: colors.bubbleTimestamp }}>
              {t('chatPreview.search.searching')}
            </Typography>
          </Box>
        ) : results === null ? (
          <Typography variant="body2" sx={{ color: colors.bubbleTimestamp, textAlign: 'center', py: 4 }}>
            {t('chatPreview.search.prompt')}
          </Typography>
        ) : results.length === 0 ? (
          <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: colors.bubbleTimestamp }}>
              {t('chatPreview.search.noResults')}
            </Typography>
            {/* A numeric query that came back as text means the id did not
                exist here, or the attachment filter excluded it. That is an
                empty result, not a failure. */}
            {/^[0-9]+$/.test(term) && (
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: colors.bubbleTimestamp }}>
                {t('chatPreview.search.noJumpTarget')}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: colors.bubbleTimestamp,
              }}
            >
              <Typography variant="caption">{t('chatPreview.search.resultsLabel')}</Typography>
              {/* Counts stay LTR whatever the UI language. */}
              <Typography variant="caption" dir="ltr" component="span">
                {results.length}
                {hasMore ? '+' : ''} · {elapsedMs} ms
              </Typography>
            </Box>

            {results.map((msg) => (
              <Box key={msg.id} sx={rowSx} onClick={() => onJump(msg.message_id)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                  <Typography variant="caption" dir="ltr" sx={{ color: colors.bubbleTimestamp }}>
                    #{msg.message_id}
                  </Typography>
                  <Typography variant="caption" dir="ltr" sx={{ color: colors.bubbleTimestamp }}>
                    {formatStamp(msg.timestamp)}
                  </Typography>
                  {msg.media && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                      <FileIcon sx={{ fontSize: 14, color: colors.bubbleTimestamp }} />
                      <Typography
                        variant="caption"
                        noWrap
                        dir="ltr"
                        sx={{ color: colors.bubbleTimestamp, maxWidth: 220 }}
                      >
                        {msg.media.file_name || msg.media.mime_type || ''}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.bubbleText,
                    direction: 'rtl',
                    textAlign: 'right',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.85rem',
                  }}
                >
                  {highlight(
                    (msg.message_text ?? '').slice(0, SNIPPET_LENGTH),
                    term,
                    colors.sidebarItemActive
                  )}
                </Typography>
              </Box>
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                <Button
                  size="small"
                  onClick={() => runSearch(term, nextBeforeId)}
                  disabled={isLoadingMore || nextBeforeId === null}
                >
                  {isLoadingMore ? t('chatPreview.search.searching') : t('chatPreview.search.loadMore')}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MessageSearchPanel;
